import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from './portalAccess';

/**
 * Reading of the refusals of the **`accounts` query** (PCG-141), the list of the
 * back-office.
 *
 * A sixth vocabulary beside `portalAccess.ts`, `moduleRefusal.ts`,
 * `catalogues/refusals.ts`, `onboarding/inviteRefusal.ts` and `users/resendRefusal.ts`,
 * reusing `refusalCode` and `Refusal` rather than restating them: **how** a business
 * code is read off an Apollo error is a fact of the API, and a sixth copy of that
 * reading is where the day's schema change would be learnt by five files out of six.
 *
 * **The whole inventory, taken from the service and not from the most visible throw.**
 * `listAccounts` refuses `NOT_AUTHENTICATED`, `INTERNAL_STAFF_ONLY` and
 * `MISSING_PERMISSION`; the `member: true` scope of the field adds
 * `NO_ACTIVE_MEMBERSHIP` before the resolver ever runs. Four codes, and the generic
 * branch stays for a fifth that has not been written yet.
 *
 * **Attention, an empty portfolio is not in this list, and that is the point.** An
 * operator whose `organization_assignment` is empty receives an **empty page**, total
 * zero, and not a refusal: the API documents it and the screen has to say it as an
 * emptiness. Read as a failure it would send somebody asking for a permission they
 * already hold.
 */
export const accountsRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'MISSING_PERMISSION':
            return {
                title: c('Accounts refusal').t`Your role does not let you manage accounts.`,
                guidance: c('Accounts refusal')
                    .t`This list is gated by the very permission its gestures need, so that nobody lands on a screen where everything refuses. The list was refused, not answered: this is not a portfolio with no account in it. Ask an administrator for that permission.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('Accounts refusal').t`This screen belongs to the back-office.`,
                guidance: c('Accounts refusal')
                    .t`Listing client accounts in order to configure them is a Freelance.com decision, so a client-side role cannot carry it whatever its permissions, otherwise an account would configure itself. The scope of the role is checked before the permission, which is why a custom role holding the right is refused too.`,
            };
        case 'NO_ACTIVE_MEMBERSHIP':
            return {
                title: c('Accounts refusal').t`Your session carries no active membership.`,
                guidance: c('Accounts refusal')
                    .t`Your identity is recognised but it is attached to nothing right now, so there is no perimeter to read the accounts through. Sign in again; if it happens once more, your own access has to be reopened.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Accounts refusal').t`Your session is no longer valid.`,
                guidance: c('Accounts refusal').t`Sign in again, then open this list back.`,
            };
        default:
            return {
                title: c('Accounts refusal').t`The list of accounts could not be read.`,
                guidance: c('Accounts refusal')
                    .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. Above all it does not say that your perimeter is empty: nothing was counted. The server's answer is below; it is what the support desk needs.`,
                // `String(error)` on an unknown throw rather than a blank: an empty
                // technical block reads as a screen that swallowed the answer.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};

/**
 * Reading of the refusals of **`accessibleAccounts`**, which is what names one account
 * on its sheet.
 *
 * It is a **shorter list than the one above and not a subset of it**, which is why it
 * is written apart rather than shared: that query carries no permission check at all
 * (`member: true` and nothing else), so `MISSING_PERMISSION` and `INTERNAL_STAFF_ONLY`
 * cannot come out of it. Pointing an operator at a permission the query never asked
 * for is the mistake this separation avoids.
 */
export const accountLookupRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'NO_ACTIVE_MEMBERSHIP':
            return {
                title: c('Accounts refusal').t`Your session carries no active membership.`,
                guidance: c('Accounts refusal')
                    .t`Your identity is recognised but it is attached to nothing right now, so there is no perimeter to look this account up in. Sign in again; if it happens once more, your own access has to be reopened.`,
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Accounts refusal').t`Your session is no longer valid.`,
                guidance: c('Accounts refusal').t`Sign in again, then open this account back.`,
            };
        default:
            return {
                title: c('Accounts refusal').t`This account could not be read.`,
                guidance: c('Accounts refusal')
                    .t`Its name and its portal access are read through the list of the accounts you may act on, and that read failed. So nothing below describes this account, and its absence here does not mean it is outside your perimeter. The server's answer is below.`,
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
